import {
  Box,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'

import { fetchCentres } from 'api/apartment'
import {
  assignRole,
  createRole,
  fetchRoleHolders,
  fetchRoles,
  setAdminCentre,
  setAlfredAccess,
  updateRole
} from 'api/roles'
import constants from 'app/constants'
import { getLoggedInUserId, showToast } from 'app/helpers'
import { useAllowedTabs } from 'app/hooks/useAccess'

import People from './People'
import RoleFormModal from './RoleFormModal'
import Roles from './Roles'

/**
 * HQ -> Roles & Permissions.
 *
 * A role is a set of Alfred tab ids. It controls what Alfred SHOWS — sidebar
 * sections, routes, in-page tabs. It is NOT an authorization boundary: the APIs
 * behind a hidden section stay callable. Deliberate (see the Role docstring in
 * oh-api); do not present this to anyone as access control.
 *
 * Roles cannot be deleted — not here, and not in the database either (User.role
 * is PROTECT and the Postgres FK is NO ACTION). Hence no delete action below.
 *
 * Assigning a role to a person happens on /people/admin and /people/teacher, so
 * the People tab here is read-only: it answers "who has which role", and lets
 * you filter by role.
 */
const MENU_NAME = 'rolesPermissions'

const TABS = [
  { id: 'roles', label: 'Roles' },
  { id: 'people', label: 'People' }
]

const TAB_KEY = 'hq.rolesPermissions.tab'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  title: { fontWeight: 700, marginBottom: theme.spacing(1) },
  tabs: { borderBottom: '1px solid #eee', marginBottom: theme.spacing(2) },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(6)
  }
}))

const RolesPermissions = () => {
  const classes = useStyles()
  const allowedTabs = useAllowedTabs(MENU_NAME, TABS)

  const [tab, setTab] = React.useState(() => {
    const saved = window.localStorage.getItem(TAB_KEY)
    return TABS.some((each) => each.id === saved) ? saved : 'roles'
  })
  const [roles, setRoles] = React.useState([])
  const [people, setPeople] = React.useState([])
  const [centres, setCentres] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [roleFilter, setRoleFilter] = React.useState('all')
  const [modalOpen, setModalOpen] = React.useState(false)
  const [editingRole, setEditingRole] = React.useState(null)
  const [saving, setSaving] = React.useState(false)
  // uuid of the person whose row is mid-save, so their controls disable rather
  // than letting a second click race the first.
  const [savingUuid, setSavingUuid] = React.useState(null)
  const currentUserUuid = React.useMemo(() => getLoggedInUserId(), [])

  React.useEffect(() => {
    window.localStorage.setItem(TAB_KEY, tab)
  }, [tab])

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const [rolesData, peopleData, centresData] = await Promise.all([
        fetchRoles(),
        fetchRoleHolders(),
        fetchCentres()
      ])
      setRoles(rolesData)
      setPeople(peopleData)
      setCentres(
        [...centresData].sort((a, b) => (a.name || '').localeCompare(b.name))
      )
    } catch (error) {
      showToast('Could not load roles', constants.ERROR)
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  const userCountByRole = React.useMemo(
    () =>
      people.reduce((counts, person) => {
        const uuid = person.role?.uuid
        if (uuid) counts[uuid] = (counts[uuid] || 0) + 1
        return counts
      }, {}),
    [people]
  )

  const openCreate = () => {
    setEditingRole(null)
    setModalOpen(true)
  }

  const openEdit = (role) => {
    setEditingRole(role)
    setModalOpen(true)
  }

  const handleDuplicate = (role) => {
    // Copy the permissions, drop the identity: a duplicate is a brand-new role,
    // and must never inherit is_super_admin (the API refuses to set it anyway).
    setEditingRole({
      uuid: null,
      name: `${role.name} (copy)`,
      description: role.description,
      permissions: role.permissions,
      is_super_admin: false
    })
    setModalOpen(true)
  }

  const handleSave = async (payload) => {
    setSaving(true)
    try {
      if (payload.uuid) {
        await updateRole(payload.uuid, {
          name: payload.name,
          description: payload.description,
          permissions: payload.permissions
        })
        showToast(`${payload.name} updated`, constants.SUCCESS)
      } else {
        await createRole({
          name: payload.name,
          description: payload.description,
          permissions: payload.permissions
        })
        showToast(`${payload.name} created`, constants.SUCCESS)
      }
      setModalOpen(false)
      setEditingRole(null)
      await load()
    } catch (error) {
      // The API is the authority on name uniqueness; surface what it said.
      const message =
        error?.response?.data?.name?.[0] ||
        error?.response?.data?.message ||
        'Could not save the role'
      showToast(message, constants.ERROR)
    } finally {
      setSaving(false)
    }
  }

  const handleViewPeople = (roleUuid) => {
    setRoleFilter(roleUuid)
    setTab('people')
  }

  // Optimistic on failure -> reload, so a rejected change never leaves the row
  // showing something the server did not accept.
  const mutatePerson = async (userUuid, fn, successMessage) => {
    setSavingUuid(userUuid)
    try {
      await fn()
      showToast(successMessage, constants.SUCCESS)
      await load()
    } catch (error) {
      const message =
        error?.response?.data?.message || 'Could not save the change'
      showToast(message, constants.ERROR)
      await load()
    } finally {
      setSavingUuid(null)
    }
  }

  const handleAssignRole = (userUuid, roleUuid) => {
    const role = roles.find((each) => each.uuid === roleUuid)
    mutatePerson(
      userUuid,
      () => assignRole(userUuid, roleUuid),
      role ? `Role set to ${role.name}` : 'Role removed'
    )
  }

  // One centre per person from Alfred, so this replaces the whole mapping. The
  // confirm dialog in People.js has already named what is being removed —
  // including the parent chat channels the person will drop out of.
  const handleAssignCentre = (userUuid, centreUuid) => {
    const centre = centres.find((each) => each.uuid === centreUuid)
    mutatePerson(
      userUuid,
      () => setAdminCentre(userUuid, centreUuid),
      centre ? `Centre set to ${centre.name}` : 'Centre removed'
    )
  }

  const handleToggleAccess = (userUuid, enabled) =>
    mutatePerson(
      userUuid,
      () => setAlfredAccess(userUuid, enabled),
      enabled ? 'Alfred access enabled' : 'Alfred access disabled'
    )

  const activeTab = allowedTabs.some((each) => each.id === tab)
    ? tab
    : allowedTabs[0]?.id

  return (
    <Box className={classes.root}>
      <Typography variant="h5" className={classes.title}>
        HQ · Roles &amp; Permissions
      </Typography>
      <Tabs
        className={classes.tabs}
        value={activeTab}
        onChange={(event, value) => setTab(value)}
        indicatorColor="primary"
        textColor="primary"
      >
        {allowedTabs.map((each) => (
          <Tab key={each.id} value={each.id} label={each.label} />
        ))}
      </Tabs>

      {loading ? (
        <Box className={classes.loading}>
          <CircularProgress />
        </Box>
      ) : activeTab === 'roles' ? (
        <Roles
          roles={roles}
          userCountByRole={userCountByRole}
          onCreate={openCreate}
          onEdit={openEdit}
          onDuplicate={handleDuplicate}
          onViewPeople={handleViewPeople}
        />
      ) : (
        <People
          people={people}
          roles={roles}
          centres={centres}
          roleFilter={roleFilter}
          currentUserUuid={currentUserUuid}
          savingUuid={savingUuid}
          onRoleFilterChange={setRoleFilter}
          onAssignRole={handleAssignRole}
          onAssignCentre={handleAssignCentre}
          onToggleAccess={handleToggleAccess}
        />
      )}

      <RoleFormModal
        open={modalOpen}
        role={editingRole}
        saving={saving}
        onClose={() => {
          setModalOpen(false)
          setEditingRole(null)
        }}
        onSave={handleSave}
      />
    </Box>
  )
}

export default RolesPermissions
